import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  Image,
  LifeBuoy,
  Paperclip,
  Plus,
  Send,
  Ticket,
  Trash2,
  Upload,
  X,
  LucideAngularModule,
} from 'lucide-angular';

type SupportAudience = 'admin' | 'builder';
type TicketPriority = 'Low' | 'Medium' | 'High';
type TicketStatus = 'Open' | 'Pending' | 'Resolved';

interface TicketAttachment {
  id: number;
  name: string;
  size: string;
  url: string;
}

interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  priority: TicketPriority;
  status: TicketStatus;
  message: string;
  requester: string;
  updatedAt: string;
  attachments: TicketAttachment[];
}

@Component({
  selector: 'app-support-ticket-panel',
  imports: [NgClass, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './support-ticket-panel.component.html',
  styleUrl: './support-ticket-panel.component.css',
})
export class SupportTicketPanelComponent {
  readonly audience = input<SupportAudience>('builder');

  readonly LifeBuoyIcon = LifeBuoy;
  readonly TicketIcon = Ticket;
  readonly UploadIcon = Upload;
  readonly ImageIcon = Image;
  readonly PaperclipIcon = Paperclip;
  readonly PlusIcon = Plus;
  readonly SendIcon = Send;
  readonly TrashIcon = Trash2;
  readonly CloseIcon = X;

  readonly subject = signal('');
  readonly category = signal('Technical issue');
  readonly priority = signal<TicketPriority>('Medium');
  readonly message = signal('');
  readonly attachments = signal<TicketAttachment[]>([]);
  readonly responseDraft = signal('');
  readonly responseAttachments = signal<TicketAttachment[]>([]);
  readonly selectedTicketId = signal('TK-1042');
  readonly tickets = signal<SupportTicket[]>([
    {
      id: 'TK-1042',
      subject: 'Drawing upload is stuck in processing',
      category: 'Project files',
      priority: 'High',
      status: 'Open',
      message: 'The latest plan set remains queued after upload.',
      requester: 'Northline Builders',
      updatedAt: 'Today, 10:20 AM',
      attachments: [],
    },
    {
      id: 'TK-1038',
      subject: 'Need invoice copy for yearly plan',
      category: 'Billing',
      priority: 'Medium',
      status: 'Pending',
      message: 'Waiting on finance review.',
      requester: 'Metro Frame Co.',
      updatedAt: 'Yesterday, 4:35 PM',
      attachments: [],
    },
  ]);

  readonly panelTitle = computed(() =>
    this.audience() === 'admin' ? 'Builder support tickets' : 'Support tickets'
  );

  readonly panelSubtitle = computed(() =>
    this.audience() === 'admin'
      ? 'Review builder requests, answer tickets, and attach response images.'
      : 'Create a request and attach screenshots or site photos.'
  );

  readonly categories = computed(() =>
    this.audience() === 'admin'
      ? ['Technical issue', 'Project files', 'Billing', 'Account access', 'Builder request']
      : ['Technical issue', 'Project files', 'Billing', 'Account access', 'General question']
  );

  readonly canCreateTicket = computed(() =>
    this.subject().trim().length > 2 && this.message().trim().length > 8
  );

  readonly selectedTicket = computed(() =>
    this.tickets().find(ticket => ticket.id === this.selectedTicketId()) ?? this.tickets()[0]
  );

  readonly canSendResponse = computed(() =>
    this.responseDraft().trim().length > 4 || this.responseAttachments().length > 0
  );

  updateSubject(value: string): void {
    this.subject.set(value);
  }

  updateCategory(value: string): void {
    this.category.set(value);
  }

  updatePriority(value: string): void {
    this.priority.set(value as TicketPriority);
  }

  updateMessage(value: string): void {
    this.message.set(value);
  }

  updateResponseDraft(value: string): void {
    this.responseDraft.set(value);
  }

  selectTicket(id: string): void {
    this.selectedTicketId.set(id);
    this.responseDraft.set('');
    this.clearResponseAttachments();
  }

  onImagesSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const nextImages = this.filesToImages(inputElement.files);

    this.attachments.update(current => [...current, ...nextImages].slice(0, 6));
    inputElement.value = '';
  }

  onResponseImagesSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const nextImages = this.filesToImages(inputElement.files);

    this.responseAttachments.update(current => [...current, ...nextImages].slice(0, 6));
    inputElement.value = '';
  }

  removeAttachment(id: number): void {
    const image = this.attachments().find(item => item.id === id);
    if (image) {
      URL.revokeObjectURL(image.url);
    }

    this.attachments.update(current => current.filter(item => item.id !== id));
  }

  removeResponseAttachment(id: number): void {
    const image = this.responseAttachments().find(item => item.id === id);
    if (image) {
      URL.revokeObjectURL(image.url);
    }

    this.responseAttachments.update(current => current.filter(item => item.id !== id));
  }

  createTicket(): void {
    if (this.audience() === 'admin' || !this.canCreateTicket()) {
      return;
    }

    const ticket: SupportTicket = {
      id: `TK-${Math.floor(1100 + Math.random() * 800)}`,
      subject: this.subject().trim(),
      category: this.category(),
      priority: this.priority(),
      status: 'Open',
      message: this.message().trim(),
      requester: this.audience() === 'admin' ? 'Builder selected by admin' : 'My builder account',
      updatedAt: 'Just now',
      attachments: this.attachments(),
    };

    this.tickets.update(current => [ticket, ...current]);
    this.subject.set('');
    this.message.set('');
    this.priority.set('Medium');
    this.category.set('Technical issue');
    this.attachments.set([]);
  }

  sendResponse(): void {
    const selected = this.selectedTicket();
    if (!selected || !this.canSendResponse()) {
      return;
    }

    this.tickets.update(current =>
      current.map(ticket =>
        ticket.id === selected.id
          ? { ...ticket, status: 'Pending', updatedAt: 'Just now' }
          : ticket
      )
    );
    this.responseDraft.set('');
    this.clearResponseAttachments();
  }

  updateTicketStatus(status: TicketStatus): void {
    const selected = this.selectedTicket();
    if (!selected) {
      return;
    }

    this.tickets.update(current =>
      current.map(ticket =>
        ticket.id === selected.id ? { ...ticket, status, updatedAt: 'Just now' } : ticket
      )
    );
  }

  priorityClass(priority: TicketPriority): string {
    const classes: Record<TicketPriority, string> = {
      Low: 'bg-talos-ink/5 text-talos-ink/55',
      Medium: 'bg-talos-accent/10 text-talos-accent',
      High: 'bg-talos-accent text-[var(--talos-bg)]',
    };

    return classes[priority];
  }

  statusClass(status: TicketStatus): string {
    const classes: Record<TicketStatus, string> = {
      Open: 'bg-talos-accent/10 text-talos-accent',
      Pending: 'bg-talos-ink/5 text-talos-ink/55',
      Resolved: 'bg-emerald-50 text-emerald-700',
    };

    return classes[status];
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private filesToImages(fileList: FileList | null): TicketAttachment[] {
    const files = Array.from(fileList ?? []).filter(file => file.type.startsWith('image/'));

    return files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: this.formatFileSize(file.size),
      url: URL.createObjectURL(file),
    }));
  }

  private clearResponseAttachments(): void {
    this.responseAttachments().forEach(image => URL.revokeObjectURL(image.url));
    this.responseAttachments.set([]);
  }
}
